import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { ToggleButton } from 'react-bootstrap';
import classnames from 'classnames';

import Control from '../../../components/form/Control';
import ToggleButtonGroup from '../../../components/form/ToggleButtonGroup';
import { softwares } from '../../../components/mapView/constants';
import text from '../../../text';
import ProjectSelectV2 from '../../../components/select/ProjectSelect/ProjectSelectV2';
import { getProjectName } from '../../../utilities/project';
import { DragDrop } from '@uppy/react';
import { useMount } from 'react-use';
import { useUploadContext } from '../../../mp/context/UploadContext';
import { File } from '../../../mp/components/File';
import { CONFIRM_MODAL } from '../../../components/modal/ConfirmModal';
import { useDispatch } from 'react-redux';
import { useDeleteUploadedFileMutation } from '../../../redux/rtk';
import { showModal } from '../../../redux/actions';

type SoftwareEntities = typeof softwares.entities;
type SoftwareKey = keyof SoftwareEntities;

function isSoftwareKey(key: string): key is SoftwareKey {
  return Object.prototype.hasOwnProperty.call(softwares.entities, key);
}

interface ProjectDetailsFormValues {
  projectId?: string | number;
  projectName?: string;
  message?: string;
  customer_software?: SoftwareKey;
}

type ProjectOption = { id?: string | number; name?: string } | null;

interface ProjectDetailsFormProps {
  disableProjectSelect?: boolean;
}

const ProjectDetailsForm: React.FC<ProjectDetailsFormProps> = ({
  disableProjectSelect = false,
}) => {
  const { state, uppy, uploadedFiles } = useUploadContext();
  const { files, uploading, error } = state;
  const [deleteUploadedFile] = useDeleteUploadedFileMutation();

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ProjectDetailsFormValues>();

  const dispatch = useDispatch();

  useMount(() => () => {
    uppy.cancelAll();
  });

  // only show files that are uploading or have errors
  const filteredFiles = useMemo(() => {
    return files.ids
      .map((id) => files.entities[id])
      .filter((file): file is NonNullable<typeof file> => {
        if (!file) return false;
        return !file.completed || Boolean(file.error);
      });
  }, [files.ids, files.entities]);

  const onDeleteUploadedFile = (id: string, filename: string) => {
    if (!values.projectId) return;
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: () =>
          deleteUploadedFile({
            projectId: Number(values.projectId),
            assetId: id,
          }),
        title: 'Confirm delete',
        body: (
          <p className='mb-0'>
            Are you sure you want to delete{' '}
            <span className='font-weight--600'>{filename}</span>?
          </p>
        ),
        okLabel: text('delete'),
      })
    );
  };

  const onRemoveFile = (id: string) => {
    uppy.removeFile(id);
  };

  return (
    <form className='ProjectDetailsForm'>
      <h5 className='required'>{text('project')}</h5>
      {/* @ts-expect-error - ProjectSelectV2 is not typed */}
      <ProjectSelectV2
        value={{
          id: values.projectId,
          name: values.projectName,
        }}
        onChange={(
          option: ProjectOption,
          { newProject = false }: { newProject?: boolean }
        ) => {
          setFieldTouched('projectName');
          if (newProject) {
            setFieldValue('projectId', undefined);
            setFieldValue('projectName', getProjectName(option));
          } else {
            if (option) {
              setFieldValue('projectId', option.id);
              setFieldValue('projectName', getProjectName(option));
            } else {
              // `option` is null if clearing the input field
              setFieldValue('projectId', undefined);
              setFieldValue('projectName', undefined);
            }
          }
        }}
        hasError={!!(touched.projectName && errors.projectName)}
        disabled={disableProjectSelect}
      />
      <p
        className={classnames('Control-Feedback', {
          isFailed: Boolean(touched.projectName && errors.projectName),
        })}
      >
        {touched.projectName ? errors.projectName : null}
      </p>

      <h5 className='required'>{text('whatSoftwareWillYouUse')}</h5>
      <div>{text('selectSoftwareFormat')}</div>
      <ToggleButtonGroup
        className='CustomerSoftware'
        type='radio'
        name='customer_software'
      >
        {softwares.result.filter(isSoftwareKey).map((key) => (
          <ToggleButton variant='outline-secondary' key={key} value={key}>
            {softwares.entities[key].name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <h5>{text('comments')}</h5>
      {/* @ts-expect-error - Control is not typed */}
      <Control
        className='Message'
        as='textarea'
        type='text'
        name='message'
        rows={3}
        isFailed={Boolean(touched.message && errors.message)}
        value={values.message}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={text('addMessageOptional')}
        validationError={touched.message ? errors.message : undefined}
      />

      <div
        className={classnames('drag-drop-upload-container d-flex flex-column', {
          disabled: uploading,
          error,
        })}
      >
        <h5>
          {text('uploadFiles')} ({text('optional')})
        </h5>
        <DragDrop
          uppy={uppy}
          note={text('dropFilesToUpload')}
          locale={{
            strings: {
              dropHereOr: text('dropHereOr'),
              browse: text('browseToUpload'),
            },
          }}
        />
        {error ? (
          <p className='Control-Feedback isFailed mb-3'>{error}</p>
        ) : null}

        <div className='mt-3'>
          {uploading ? (
            <p className='files-header mb-0'>
              Uploading - {files.ids.length}/{files.ids.length} files
            </p>
          ) : null}

          {filteredFiles.map((file) => (
            <File
              key={file.id}
              file={file}
              onClick={() => onRemoveFile(file.id)}
            />
          ))}

          {uploadedFiles.length > 0 && (
            <>
              <p
                className={classnames('mt--12 mb-0 font-weight--400', {
                  'mt-3': filteredFiles.length > 0,
                })}
              >
                Uploaded files
              </p>
              {uploadedFiles.map((file) => (
                <File
                  key={file.id}
                  file={file}
                  onClick={() => onDeleteUploadedFile(file.id, file.filename)}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default ProjectDetailsForm;
